import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { MODULES } from "@/config/modules";
import { PageHeader, EmptyState, TableSkeleton } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Search, ChevronLeft, ChevronRight, Inbox } from "lucide-react";

export default function ModulePage({ moduleKey }) {
  const cfg = MODULES[moduleKey];
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const fetch = useCallback(async () => {
    setData(null);
    try {
      const { data } = await api.get(cfg.endpoint, {
        params: { q: cfg.searchable && q ? q : undefined, page, page_size: 20 },
      });
      setData(data);
    } catch (e) { toast.error(apiErr(e)); setData({ items: [], total: 0, pages: 0 }); }
  }, [cfg.endpoint, cfg.searchable, q, page]);

  useEffect(() => { setPage(1); }, [moduleKey]);
  useEffect(() => { fetch(); }, [fetch]);

  return (
    <div>
      <PageHeader title={cfg.title} subtitle={data ? `${data.total} record${data.total === 1 ? "" : "s"}` : cfg.subtitle} />

      {cfg.searchable && (
        <Card className="p-4 mb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid={`${moduleKey}-search`} placeholder="Search…" value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }} className="pl-9" />
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        {!data ? <div className="p-4"><TableSkeleton cols={cfg.columns.length} /></div>
          : data.items.length === 0 ? <EmptyState icon={Inbox} title={`No ${cfg.title.toLowerCase()} yet`} description="Records will appear here as your team works." />
            : (
              <div className="overflow-x-auto scrollbar-thin">
                <Table>
                  <TableHeader><TableRow>{cfg.columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}</TableRow></TableHeader>
                  <TableBody>
                    {data.items.map((r) => (
                      <TableRow key={r.id} data-testid={`${moduleKey}-row-${r.id}`} className="hover:bg-accent/40">
                        {cfg.columns.map((c) => (
                          <TableCell key={c.key} className="text-sm">{c.render ? c.render(r) : (r[c.key] ?? "—")}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

        {data && data.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <span className="text-sm text-muted-foreground">Page {data.page} of {data.pages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
